import { useAdminData, type SiteConfig } from "./admin/AdminDataContext";

/**
 * Reads the admin theme configuration and injects CSS custom properties
 * into :root so that the entire storefront picks up color/font changes
 * made via the admin Theme tab — no page reload required.
 *
 * This component renders an invisible <style> tag; it has no visual output.
 */

const FONT_IMPORT_MAP: Record<string, string> = {
  "Cormorant Garamond": "Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400",
  "Jost": "Jost:wght@300;400;500",
  "Inter": "Inter:wght@300;400;500;600",
  "Playfair Display": "Playfair+Display:ital,wght@0,400;0,500;0,600;1,400",
  "DM Serif Display": "DM+Serif+Display:wght@400",
  "Lora": "Lora:ital,wght@0,400;0,500;0,600;1,400",
  "Source Sans 3": "Source+Sans+3:wght@300;400;500;600",
  "Noto Sans JP": "Noto+Sans+JP:wght@300;400;500",
  "Noto Serif JP": "Noto+Serif+JP:wght@400;500;600",
  "Raleway": "Raleway:wght@300;400;500;600",
  "Poppins": "Poppins:wght@300;400;500;600",
  "Libre Baskerville": "Libre+Baskerville:ital,wght@0,400;0,700;1,400",
};

const BORDER_RADIUS_MAP: Record<string, string> = {
  full: "9999px",
  lg: "12px",
  md: "8px",
  sm: "4px",
  none: "0px",
};

function isValidCssColor(v: string): boolean {
  return /^#[0-9a-fA-F]{3,8}$/.test(v) || /^rgba?\(/.test(v) || /^hsla?\(/.test(v);
}

function sanitize(v: string): string {
  return isValidCssColor(v) ? v : "";
}

export function ThemeInjector() {
  const { siteConfig } = useAdminData();
  const theme = siteConfig.theme;
  if (!theme) return null;

  const c = theme.colors;
  const n = theme.navbar;
  const b = theme.buttons;
  const f = theme.fonts;

  // Build Google Fonts import for selected fonts
  const fontsToLoad = new Set<string>();
  if (f?.heading && FONT_IMPORT_MAP[f.heading]) fontsToLoad.add(FONT_IMPORT_MAP[f.heading]);
  if (f?.body && FONT_IMPORT_MAP[f.body]) fontsToLoad.add(FONT_IMPORT_MAP[f.body]);
  const fontImport = fontsToLoad.size > 0
    ? `@import url('https://fonts.googleapis.com/css2?${[...fontsToLoad].map(f => `family=${f}`).join("&")}&display=swap');`
    : "";

  const headingFont = f?.heading ? `'${f.heading}', serif` : "";
  const bodyFont = f?.body ? `'${f.body}', sans-serif` : "";
  const btnRadius = BORDER_RADIUS_MAP[b?.borderRadius || "full"] || "9999px";

  const css = `
${fontImport}
:root {
  ${c?.background ? `--moecha-bg: ${sanitize(c.background)};` : ""}
  ${c?.foreground ? `--moecha-fg: ${sanitize(c.foreground)};` : ""}
  ${c?.primary ? `--moecha-primary: ${sanitize(c.primary)};` : ""}
  ${c?.accent ? `--moecha-accent: ${sanitize(c.accent)};` : ""}
  ${c?.muted ? `--moecha-muted: ${sanitize(c.muted)};` : ""}
  ${c?.border ? `--moecha-border: ${sanitize(c.border)};` : ""}
  ${c?.card ? `--moecha-card: ${sanitize(c.card)};` : ""}
  ${n?.background ? `--moecha-nav-bg: ${sanitize(n.background)};` : ""}
  ${n?.scrolledBackground ? `--moecha-nav-scrolled-bg: ${sanitize(n.scrolledBackground)};` : ""}
  ${n?.textColor ? `--moecha-nav-text: ${sanitize(n.textColor)};` : ""}
  ${b?.primaryBg ? `--moecha-btn-bg: ${sanitize(b.primaryBg)};` : ""}
  ${b?.primaryText ? `--moecha-btn-text: ${sanitize(b.primaryText)};` : ""}
  --moecha-btn-radius: ${btnRadius};
  ${headingFont ? `--moecha-font-heading: ${headingFont};` : ""}
  ${bodyFont ? `--moecha-font-body: ${bodyFont};` : ""}
}
`;

  return <style dangerouslySetInnerHTML={{ __html: css }} />;
}

/** Helper to get the resolved theme value, with fallback to MOECHA defaults */
export function useTheme() {
  const { siteConfig } = useAdminData();
  const t = siteConfig.theme;
  return {
    bg: t?.colors?.background || "#F7F5F1",
    fg: t?.colors?.foreground || "#2D2A26",
    primary: t?.colors?.primary || "#6F7F63",
    accent: t?.colors?.accent || "#C7A76C",
    muted: t?.colors?.muted || "#6F675F",
    border: t?.colors?.border || "#E6E0D7",
    card: t?.colors?.card || "#FFFFFF",
    navBg: t?.navbar?.background || "#F7F5F1",
    navScrolledBg: t?.navbar?.scrolledBackground || "rgba(247,245,241,0.95)",
    navText: t?.navbar?.textColor || "#2D2A26",
    btnBg: t?.buttons?.primaryBg || "#6F7F63",
    btnText: t?.buttons?.primaryText || "#FFFFFF",
    btnRadius: BORDER_RADIUS_MAP[t?.buttons?.borderRadius || "full"] || "9999px",
    fontHeading: t?.fonts?.heading ? `'${t.fonts.heading}', serif` : "'Cormorant Garamond', serif",
    fontBody: t?.fonts?.body ? `'${t.fonts.body}', sans-serif` : "'Jost', sans-serif",
  };
}
